import { useEffect, useMemo } from "react";

import clsx from "clsx";
import { twMerge } from "tailwind-merge";

import {
  useSideNavContext,
  useSideNavigation,
} from "@pages/onboarding/onboarding-nav/onboarding-nav-item";
import { useOnboardingStep } from "@pages/onboarding/onboarding-nav/use-onboarding-number";
import { ServerDiagnosticsDisplay } from "@pages/settings/pixel-diagnostics";
import { PrimaryButton } from "@shared/buttons";

import { ApiKeys } from "../components/api-keys";
import { ConfirmCheckbox } from "../components/confirm-checkbox";
import { LinkClipboardButton } from "../components/link-clipboard-button";
import { Table } from "../components/table";

import {
  OrderTagExclusionDocPanel,
  OrderTagExclusions,
} from "./order-tag-exclusions";
import { OrderTagsConfirmation } from "./order-tags-confirmation";
import { type OrdersApiStepMetadata } from "./step-metadata";

export const OrderApiTags = ({
  stepMetadata,
  updateStepMetadata,
  aTitleRef,
  bTitleRef,
  cTitleRef,
  dTitleRef,
  eTitleRef,
  isMarkedComplete,
}: {
  stepMetadata: OrdersApiStepMetadata;
  updateStepMetadata: (data: OrdersApiStepMetadata) => void;
  aTitleRef: (node: any) => void;
  bTitleRef: (node: any) => void;
  cTitleRef: (node: any) => void;
  dTitleRef: (node: any) => void;
  eTitleRef: (node: any) => void;
  isMarkedComplete?: boolean;
}) => {
  const { stepDictionary } = useOnboardingStep();
  const stepIndex = stepDictionary["orders-api"].index;
  const subnavItems = useMemo(() => {
    if (stepMetadata.doNotUseOrderTags) {
      return [
        {
          label: `${stepIndex}a. Add Order Tracking to Northbeam using the ‘Orders API’`,
          subStep: "a",
        },
        {
          label: `${stepIndex}b. Tell us if your brand will use Order Tags`,
          subStep: "b",
        },

        {
          label: `${stepIndex}c. Verify no Orders API issues`,
          subStep: "e",
        },
      ];
    }
    return [
      {
        label: `${stepIndex}a. Add Order Tracking to Northbeam using the ‘Orders API’`,
        subStep: "a",
      },
      {
        label: `${stepIndex}b. Tell us if your brand will use Order Tags`,
        subStep: "b",
      },
      {
        label: `${stepIndex}c. Configure Order Tagging through the ‘Northbeam Orders API’`,
        subStep: "c",
      },
      {
        label: `${stepIndex}d. Configure Northbeam Order Exclusions (optional)`,
        subStep: "d",
      },
      {
        label: `${stepIndex}e. Verify no Orders API issues`,
        subStep: "e",
      },
    ];
  }, [stepIndex, stepMetadata.doNotUseOrderTags]);
  const { setSubNavItems } = useSideNavContext();
  const [_, setSearchParams] = useSideNavigation();

  useEffect(() => {
    setSubNavItems(subnavItems);
  }, [setSubNavItems, subnavItems]);

  return (
    <div
      className="
          [&_h1]:m-0 [&_h1]:p-0 [&_h1]:font-semibold [&_h1]:text-2xl
          [&_h2]:m-0 [&_h2]:p-0 [&_h2]:font-semibold [&_h2]:text-lg [&_h2]:leading-6
          "
    >
      <div
        ref={aTitleRef}
        className="px-10 pt-10 pb-20 flex-col justify-start items-start gap-6 inline-flex border-b-gray-1 border-b scroll-m-10"
      >
        <h1>{stepIndex}a. Add Order Tracking using the ‘Orders API’</h1>
        <DocumentationInfo
          docTitle="Learn about the 'Orders API'"
          link="https://docs.northbeam.io/docs/orders-api"
          iconClassName="fa-sharp fa-solid fa-book"
        />
        <Divider />
        <div className="w-full flex flex-col gap-4">
          <h2>
            1. Use the ‘Data-client-ID’ and ‘API Key’ to upload your orders to
            Northbeam
          </h2>
          <ApiKeys />
        </div>
        <Divider />
        <div className="flex flex-col gap-10">
          <div className="flex flex-col gap-4">
            <h2>2. Test the ‘Orders API’</h2>
            <p>
              No need to be a full blown developer, just insert your API keys
              and populate an example payload with some sample data (use our
              example values if unsure) to make an API call to our development
              or production endpoints. The Reference page also features example
              code for Shell, Ruby, PHP, Python and my other languages.
            </p>
            <PrimaryButton
              onClick={() => {
                window.open(
                  "https://docs.northbeam.io/reference/post_orders",
                  "_blank",
                );
              }}
            >
              <div className="flex gap-2 items-center text-xs">
                <i className="fa-sharp fa-regular fa-share" />
                <span className="font-semibold uppercase leading-tight tracking-tight">
                  Test In the Northbeam Orders API reference
                </span>
              </div>
            </PrimaryButton>
          </div>
          <div>
            <b>Testing Keys</b>
            <p>
              When testing, you can use the following values without the need
              for production keys. In the testing environment, no orders are
              saved and we only return the results of validation. Testing with
              your user keys is also supported, though the development database
              is purged from time to time to limit storage bloat so it should
              only be treated as a testing environment and not a stable one.
            </p>
            <Table className="w-full mt-4">
              <thead>
                <tr>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {[
                  "--header 'Authorization: None'",
                  "--header 'Content-Type: application/json'",
                  "--header 'Data-Client-ID: test'",
                ].map((value, idx) => (
                  <tr
                    key={idx}
                    className="border-t odd:bg-cosmic-5 even:bg-white"
                  >
                    <td>
                      <code>{value}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <div>
            <b>Endpoint URLs:</b>
            <Table className="w-full mt-4" tableLayout="auto">
              <thead>
                <tr>
                  <th className=" border-r">URL</th>
                  <th className=" border-r">Purpose</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t odd:bg-cosmic-5 even:bg-white">
                  <td className="border-r">
                    <div className="w-full inline-flex justify-between items-center gap-2">
                      <code>https://api-uat.northbeam.io/v2/orders</code>
                      <LinkClipboardButton
                        value="https://api-uat.northbeam.io/v2/orders"
                        name="URL"
                      />
                    </div>
                  </td>
                  <td className="border-r">Testing</td>
                  <td>For testing the API endpoint</td>
                </tr>
                <tr className="border-t odd:bg-cosmic-5 even:bg-white">
                  <td className="border-r">
                    <div className="w-full inline-flex justify-between items-center gap-2">
                      <code>https://api.northbeam.io/v2/orders</code>
                      <LinkClipboardButton
                        value="https://api.northbeam.io/v2/orders"
                        name="URL"
                      />
                    </div>
                  </td>
                  <td className="border-r">Production</td>
                  <td>
                    Orders sent via this base URL will be used for ad
                    attribution (MTA) and predictive analytics (MMM)
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
          <Divider />
          <div className="w-full flex flex-col gap-4">
            <h2>3. Backfill your past Orders</h2>
            <p>
              It is important to backfill you past orders so that you can
              understand how your business was doing in the past.
            </p>
            <DocumentationInfo
              docTitle="Learn more about Orders Backfill"
              link="https://docs.northbeam.io/docs/orders-api#backfilling-past-orders"
              iconClassName="fa-sharp fa-solid fa-book"
            />
          </div>
          <Divider />
          <div className="flex flex-col gap-4">
            <h2>
              4. Confirm you’re finished setting up and testing the ‘Orders API’
            </h2>
            <ConfirmCheckbox
              id="orders-api-setup-confirmation"
              checked={!!stepMetadata.isApiTestingComplete}
              disabled={isMarkedComplete}
              onChange={(checked) => {
                updateStepMetadata({
                  ...stepMetadata,
                  isApiTestingComplete: checked,
                });
              }}
              showSuccess
            >
              I’ve finished setting up and testing the ‘Orders API’
            </ConfirmCheckbox>
          </div>
        </div>
      </div>
      <div
        ref={bTitleRef}
        className="w-full px-10 pt-10 pb-20 flex-col justify-start items-start gap-6 inline-flex border-b-gray-1 border-b scroll-m-10 "
      >
        <OrderTagsConfirmation
          stepMetadata={stepMetadata}
          updateStepMetadata={updateStepMetadata}
          isMarkedComplete={isMarkedComplete}
        />
      </div>
      {stepMetadata.doNotUseOrderTags || (
        <>
          <div
            ref={cTitleRef}
            className="w-full px-10 pt-10 pb-20 flex-col justify-start items-start gap-6 inline-flex border-b-gray-1 border-b scroll-m-10"
          >
            <h1>
              {stepIndex}c. Configure order tagging through the ‘Northbeam
              Orders API’
            </h1>
            <div className="text-interstellar+0 text-lg">
              An Order Tag is a way of tagging an Order with Customer Specified
              attributes.
            </div>
            <ol className="list-decimal pl-4">
              <li className="text-interstellar+0 pb-3 text-lg">
                You can have as many Order Tags as you would like.
              </li>
              <li className="text-interstellar+0 pb-3 text-lg">
                After you've completed onboarding, you'll be able to manage
                Order Tag Exclusions in 'Settings'.
              </li>
              <li className="text-interstellar+0  text-lg">
                Some suggested Order Tags are{" "}
                <code className="border border-gray-0 px-2 rounded">
                  ["exclude", "draft"]
                </code>{" "}
                or{" "}
                <code className="border border-gray-0 px-2 rounded">
                  ["return", "canceled"]
                </code>
              </li>
            </ol>
            <Table className="w-full" tableLayout="auto">
              <thead>
                <tr>
                  <th className="col-span-2">Common Order Tags</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t odd:bg-cosmic-5 even:bg-white">
                  <td className="border-r">In-App orders</td>
                  <td className="py-2 space-y-0.5">
                    <p className="m-0 p-0">
                      Orders from a native shopping experience (e.g. TikTok
                      Shops)
                    </p>
                    <p className="m-0 p-0">
                      *If you are a Shopify user, Meta Shops will be attributed
                      to your Shopify store
                    </p>
                  </td>
                </tr>
                <tr className="border-t odd:bg-cosmic-5 even:bg-white">
                  <td className="border-r">Subscriptions</td>
                  <td>
                    Manually created orders (draft orders, wholesale, orders by
                    a sales rep)
                  </td>
                </tr>
                <tr className="border-t odd:bg-cosmic-5 even:bg-white">
                  <td className="border-r">
                    Returns (used to exclude from Attribution)
                  </td>
                  <td>Marketplace or app orders (e.g. Tapcart, Shop App)</td>
                </tr>
                <tr className="border-t odd:bg-cosmic-5 even:bg-white">
                  <td className="border-r">
                    Cancelled orders (used to exclude from Attribution)
                  </td>
                  <td>
                    Third party checkout orders (e.g. ClickFunnels, Walmart,
                    etc.)
                  </td>
                </tr>
              </tbody>
            </Table>
            <Divider />
            <div className="flex flex-col gap-2">
              <h2>1. Set up Order Tagging</h2>
              <p>
                When uploading your orders via the ‘Northbeam Orders API’,
                ensure you pass in any order tags.
              </p>
              <p>
                You can do this by adding your Order Tags to the{" "}
                <code>order_tags</code> attribute in each Order object when
                calling the POST API request for{" "}
                <a
                  href="https://docs.northbeam.io/reference/post_orders"
                  target="_blank"
                  rel="noreferrer"
                >
                  creating orders
                </a>
                .
              </p>
            </div>
            <Divider />
            <div className="flex flex-col gap-4">
              <h2>
                2. Test to confirm that order tags are passing through the
                Orders API endpoint
              </h2>
              <p>
                For more information about testing the ‘Orders API’ see{" "}
                <span
                  className="underline text-primary cursor-pointer hover:text-active"
                  onClick={() => {
                    setSearchParams({
                      subStep: "a",
                      noScroll: false,
                    });
                  }}
                >
                  {stepIndex}a, step 2 (above)
                </span>
              </p>
              <ConfirmCheckbox
                id="confirm-order-tags"
                checked={!!stepMetadata.apiOrderTagsConfirmed}
                disabled={isMarkedComplete}
                onChange={(checked) => {
                  updateStepMetadata({
                    ...stepMetadata,
                    apiOrderTagsConfirmed: checked,
                  });
                }}
                showSuccess
              >
                I’ve confirmed that order tags are passing through the ‘Orders
                API’ endpoint
              </ConfirmCheckbox>
            </div>
          </div>
          <div
            ref={dTitleRef}
            className="w-full px-10 pt-10 pb-20 flex-col justify-start items-start gap-6 inline-flex border-b-gray-1 border-b scroll-m-10"
          >
            <h1>{stepIndex}d. Configure Northbeam Order Exclusions</h1>
            <p className="mb-2">
              Use Order Tags to exclude certain purchases from attribution.{" "}
            </p>
            <OrderTagExclusionDocPanel className="mt-2" />
            <div className="w-full flex flex-col gap-4">
              <h2>1. Configure Order Exclusions (optional)</h2>
              <div>
                If you do not have an Orders you would like to Exclude from
                Attribution, move to Step 2 of {stepIndex}d.
              </div>
              <OrderTagExclusions disabled={isMarkedComplete} />
            </div>
            <Divider />
            <div className="w-full flex flex-col gap-4">
              <h2>2. Confirm you’re finished with Order Exclusions</h2>
              <ConfirmCheckbox
                id="confirm-order-exclusions"
                checked={!!stepMetadata.orderExclusionsComplete}
                disabled={isMarkedComplete}
                onChange={(checked) => {
                  updateStepMetadata({
                    ...stepMetadata,
                    orderExclusionsComplete: checked,
                  });
                }}
                showSuccess
              >
                I’m finished with Order Exclusions
              </ConfirmCheckbox>
            </div>
          </div>
        </>
      )}
      <div
        ref={eTitleRef}
        className="w-full px-10 pt-10 pb-20 flex-col justify-start items-start gap-6 inline-flex border-b-gray-1 border-b scroll-m-10"
      >
        <h1>
          {stepIndex}
          {stepMetadata.doNotUseOrderTags ? "c" : "e"}. Verify there are no
          errors
        </h1>
        <p className="mb-2">
          If you do encounter errors, please address them using the Orders API
          Error table and graph below.
        </p>
        <div className="w-full">
          <ServerDiagnosticsDisplay spendApi={false} ordersApi />
        </div>
      </div>
    </div>
  );
};

const Divider = () => <hr className="w-full bg-gray" />;

export const DocumentationInfo = ({
  docTitle,
  link,
  className,
  iconClassName,
}: {
  docTitle: string;
  link: string;
  className?: string;
  iconClassName?: string;
}) => (
  <div
    className={clsx(
      "border border-gray-0 rounded-lg bg-cosmic-5 p-6 w-full",
      className,
    )}
  >
    <div className="flex items-center gap-2 pb-4">
      <div className="w-[20px] h-[20px] bg-cosmic rounded-full border border-black flex justify-center items-center">
        <i
          className={twMerge(" fa-xs", iconClassName)}
          style={{ color: "#ffffff" }}
        ></i>
      </div>
      <div className="text-lg font-semibold">{docTitle}</div>
    </div>
    <a
      href={link}
      target="_blank"
      className="h-8 px-4 py-1.5 bg-white rounded-2xl border border-gray-0 justify-center items-center gap-2 inline-flex no-underline"
      rel="noreferrer"
    >
      <i className="fa-file fa-regular text-cosmic text-xs" />
      <div className="text-interstellar+2 text-xs font-semibold uppercase leading-tight tracking-tight">
        Read the documentation
      </div>
    </a>
  </div>
);
